import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { billingAPI, publicAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { ArrowUpIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const PLAN_FEATURES = {
  starter: {
    name: 'Starter',
    messages: '2,000',
    documents: '50',
    price: '$39',
  },
  pro: {
    name: 'Pro',
    messages: '10,000',
    documents: '300',
    price: '$99',
  },
  enterprise: {
    name: 'Enterprise',
    messages: '50,000+',
    documents: '2,000+',
    price: '$500+',
    features: [
      'API access',
      'Custom integrations',
      'SSO',
      'Advanced analytics',
      'Team roles & permissions',
      'Priority support & SLA',
    ],
  },
};

export default function Upgrade() {
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchSubscriptionStatus } = useAuth();

  const inviteToken = searchParams.get('invite');

  // Handle success redirect from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const isUpgrade = searchParams.get('upgrade');

    if (sessionId && isUpgrade) {
      // Upgrade checkout completed, refresh subscription and redirect
      fetchSubscriptionStatus().then(() => {
        toast.success('Your plan has been upgraded!');
        navigate('/admin/billing');
      });
    }
  }, [searchParams, navigate, fetchSubscriptionStatus]);

  // Validate invite token on load
  useEffect(() => {
    const validateInvite = async () => {
      if (!inviteToken) {
        setError('No invite link provided. Please use a valid upgrade link.');
        setLoading(false);
        return;
      }

      try {
        const response = await publicAPI.validateInviteToken(inviteToken);

        if (!response.valid) {
          setError(response.error || 'This invite link is invalid or has expired.');
          setLoading(false);
          return;
        }

        setInviteData({
          plan: response.plan,
          trialDays: response.trial_days,
          name: response.name,
        });
      } catch (err) {
        console.error('Invite validation error:', err);
        setError('Failed to validate invite link. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    validateInvite();
  }, [inviteToken]);

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    setError(null);

    try {
      const response = await billingAPI.createUpgradeCheckout({
        invite_token: inviteToken,
        billing_cycle: 'monthly',
        success_url: `${window.location.origin}/admin/upgrade?invite=${inviteToken}`,
        cancel_url: `${window.location.origin}/admin/upgrade?invite=${inviteToken}`,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url;
    } catch (err) {
      console.error('Upgrade checkout error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to start upgrade checkout. Please try again.';
      setError(errorMessage);
      setCheckoutLoading(false);
    }
  };

  const planInfo = inviteData?.plan ? PLAN_FEATURES[inviteData.plan] : null;

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
        }}
      >
        <div className="flex items-center gap-2 text-gray-600">
          <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="font-manrope">Validating invite...</span>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div
        className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        style={{
          background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
        }}
      >
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XMarkIcon className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-jakarta text-2xl font-bold text-gray-900 mb-2">
              Invalid Upgrade Link
            </h1>
            <p className="text-gray-600 font-manrope mb-6">
              {error}
            </p>
            <Link
              to="/admin/billing"
              className="inline-flex items-center justify-center rounded-xl bg-cleona-600 px-6 py-3 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope"
            >
              Back to Billing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
      }}
    >
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-cleona-600 px-4 py-2 mb-6">
            <span className="text-white font-manrope font-semibold text-sm">Plan Upgrade</span>
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white">
              <ArrowUpIcon className="w-3.5 h-3.5 text-cleona-600" />
            </span>
          </div>

          <h1 className="font-jakarta text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Upgrade to {planInfo?.name}
          </h1>
          <p className="text-gray-600 font-manrope">
            {inviteData?.name ? `You've been invited to upgrade via "${inviteData.name}"` : 'Complete your plan upgrade'}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Plan Details */}
          <div className="border border-cleona-200 rounded-xl p-6 mb-6 bg-cleona-50">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-500 font-manrope">New Plan</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-cleona-600 text-white">
                {planInfo?.name}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-900 font-jakarta mb-2">
              {planInfo?.price}<span className="text-base font-normal text-gray-500">/month</span>
            </div>
            <div className="text-sm text-gray-600 font-manrope">
              {planInfo?.messages} messages/month &bull; {planInfo?.documents} documents
            </div>
          </div>

          {/* Enterprise Features */}
          {inviteData?.plan === 'enterprise' && planInfo?.features && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 font-manrope">
                Enterprise Features
              </h3>
              <ul className="space-y-2">
                {planInfo.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-gray-600 font-manrope">
                    <CheckIcon className="w-4 h-4 text-cleona-600 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* What happens */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-cleona-100 rounded-full flex items-center justify-center">
                <CheckIcon className="w-4 h-4 text-cleona-600" />
              </div>
              <p className="text-sm text-gray-600 font-manrope">
                Immediate access to all {planInfo?.name} features
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-cleona-100 rounded-full flex items-center justify-center">
                <CheckIcon className="w-4 h-4 text-cleona-600" />
              </div>
              <p className="text-sm text-gray-600 font-manrope">
                Your existing data and settings are preserved
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-cleona-100 rounded-full flex items-center justify-center">
                <CheckIcon className="w-4 h-4 text-cleona-600" />
              </div>
              <p className="text-sm text-gray-600 font-manrope">
                Billing starts from today
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm font-manrope">
              {error}
            </div>
          )}

          <button
            onClick={handleUpgrade}
            disabled={checkoutLoading}
            className="w-full rounded-xl bg-cleona-600 px-6 py-4 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {checkoutLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              `Upgrade to ${planInfo?.name}`
            )}
          </button>

          <Link
            to="/admin/billing"
            className="block w-full text-center mt-3 text-sm text-gray-500 hover:text-gray-700 font-manrope"
          >
            Cancel and go back
          </Link>
        </div>

        {/* Footer note */}
        <p className="text-center text-sm text-gray-500 mt-6 font-manrope">
          Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );
}
